/*
 * @description:  订单联系人（占用字段：orderContact）、其它需求（占用字段：otherRequire）、说明文案（占用字段：illustrate）、是否同意《货物托运服务协议》（占用字段：agreeGoodsTransportAgreement）、纸质回单送回数据（占用字段：paperReceiptData）
 */
import type { AddressType, AggregateDataType, InsuranceAmountRangeItem, PickupReceivingCodeType } from '@/pages/order-center/type';
import type { PaperReceiptDataType } from '@/pages/order-center-order-confirm/type';
import type { I } from '@/types/project';
import { useState, useCallback, useMemo, useRef } from 'react';
import { useModel } from 'umi';
import { cloneDeep } from 'lodash-es';
import type { VehicleItem, VehicleStdItem } from '@/pages/order-center-order-home/components/lindan-car-info';
import type { CarSpecificInfoType } from '@/pages/order-center-order-home/components/lindan-car-info/type';

export default function OrderCenterSameCitReset() {

  // 循环引用类型推断导致的类型错误，使用 as 断言
  const { addrLists } = useModel('order-center-same-city-route') as { addrLists: any[] };

  const { setGoodsData } = useModel('order-center-same-city-use-car-goods-info');

  const { updateLindanCarBargainPriceInfo }  = useModel('order-center-same-city-calc')
  // 其他需求下拉选择
  const [otherRequire, setOtherRequire] = useState<number[]>([]);
  const setOtherRequireM = useCallback((val) => {
    setOtherRequire(val);
  }, []);


  // ops配置
  const [agreeGate, setAgreeGate] = useState<AggregateDataType>();
  const setAgreeGateData = useCallback((val: AggregateDataType) => {
    setAgreeGate(val);
  }, []);

  const [pickupCode, setPickupCode] = useState<I.YesOrNoType>();
  const [deliveryCode, setDeliveryCode] = useState<I.YesOrNoType>();


  // 保险选中类型：quick-快捷保价 custom-自定义 null-未选中
  const [insuranceSelectedType, setInsuranceSelectedType] = useState<'quick' | 'custom'>();

  // 快捷保价选项数据（价值、保费）
  const [quickInsuranceData, setQuickInsuranceData] = useState<InsuranceAmountRangeItem & {
    index: number;
  }>();

  // 自定义输入数据（价值、保费）
  const [customInsuranceData, setCustomInsuranceData] = useState<InsuranceAmountRangeItem>();

  const resetPickUpReceivingCode = () => {
    setPickupCode(undefined);
    setDeliveryCode(undefined);
  }

  // 纸质回单送回数据
  const [paperReceiptInputData, setPaperReceiptInputData] = useState<PaperReceiptDataType | null>(null);
  // 纸质回单弹窗是否已确认过（用于区分用户主动确认 vs 默认使用发货地地址）
  const [paperReceiptConfirmed, setPaperReceiptConfirmed] = useState<boolean>(false);


  const [specLoading, setSpecLoading] = useState(false);

  // 设置纸质回单数据
  const setPaperReceiptInputDataCb = useCallback((val: PaperReceiptDataType | null) => {
    setPaperReceiptInputData(val);
  }, []);

  // 设置纸质回单确认状态
  const setPaperReceiptConfirmedCb = useCallback((val: boolean) => {
    setPaperReceiptConfirmed(val);
  }, []);

  // 清空纸质回单数据
  const clearPaperReceiptData = () => {
    setPaperReceiptInputData(null);
    setPaperReceiptConfirmed(false);
  };

  const clearInsuranceData = () => {
    setInsuranceSelectedType(undefined);
    setQuickInsuranceData(undefined);
    setCustomInsuranceData(undefined);
  };
  const carSpecificInfo = useRef<CarSpecificInfoType>();

  /** 零担车长车型：选中的最小车长（完整 VehicleItem；null 为不限） */
  const [lindanCarSelectedVehicle, setLindanCarSelectedVehicle] = useState<VehicleItem>();
  /** 零担车长车型：选中的车型要求，空数组为不限 */
  const [lindanCarSelectedStdList, setLindanCarSelectedStdList] = useState<VehicleStdItem[]>([]);

  const setLindanCarSelectedVehicleCb = useCallback((v?: VehicleItem) => {
    setLindanCarSelectedVehicle(v);
  }, []);

  const setLindanCarSelectedStdListCb = useCallback((list: VehicleStdItem[]) => {
    setLindanCarSelectedStdList(list);
  }, []);

  //  清空数据
  const restReset = () => {
    setOtherRequire([]);
    resetPickUpReceivingCode();
    clearPaperReceiptData();
    clearInsuranceData();
  };

  const set = useCallback((val: { otherRequire: number[] }) => {
    setOtherRequire(val.otherRequire);
  }, []);


  const initPickUpReceivingCode = (pickupReceivingCode: PickupReceivingCodeType) => {
    const { pickupCodeStatus, receivingCodeStatus } =
      pickupReceivingCode || {};
    // 初始化逻辑
    setPickupCode(pickupCodeStatus === 1 ? 1 : 0);

    setDeliveryCode(receivingCodeStatus === 1 ? 1 : 0);

  };

  const paperReceiptData: PaperReceiptDataType | null = useMemo(() => {
    // 如果已确认过 & 地址 name 存在，则使用 paperReceiptInputData 的数据
    if (paperReceiptConfirmed && paperReceiptInputData?.receiptAddrInfo?.name) {
      return paperReceiptInputData;
    }

    // 否则使用发货地地址作为默认值（驼峰命名）
    const firstAddr: AddressType = addrLists?.[0] || {};
    if (!firstAddr?.addr && !firstAddr?.name) return null;

    return {
      receiptBackType: null,
      receiptAddrInfo: cloneDeep(firstAddr),
    };
  }, [paperReceiptConfirmed, paperReceiptInputData, addrLists]);


  const hitInsurance = useMemo(() => {
    const { insuranceShowStatus, insuranceVehicleType } = agreeGate?.insuranceShow || {};
    return insuranceShowStatus === 1 && [1, 0].includes(insuranceVehicleType || 0);
  }, [agreeGate?.insuranceShow]);

  const selectedInsuranceData = useMemo(() => {
    if (!hitInsurance) {
      return undefined;
    }
    if (insuranceSelectedType === 'quick' && quickInsuranceData) {
      return quickInsuranceData;
    }
    if (insuranceSelectedType === 'custom' && customInsuranceData) {
      return customInsuranceData;
    }
    return undefined;
  }, [insuranceSelectedType, quickInsuranceData, customInsuranceData, hitInsurance]);
  const handleLindanCarReset = () => {
    setLindanCarSelectedVehicle(undefined);
    carSpecificInfo.current = undefined;
  }

  // 显示文本
  const specInfoDisplayText = useMemo(() => {
    const noVehicle = !lindanCarSelectedVehicle;
    const noStd = lindanCarSelectedStdList.length === 0;
    const stdJoined = lindanCarSelectedStdList.map((std) => std.name).join('/');

    if (noVehicle && noStd) {
      return '不限车长车型';
    }
    if (noVehicle) {
      return `不限车长，${stdJoined}`;
    }
    if (noStd) {
      return `${lindanCarSelectedVehicle.name}以上，不限车型`;
    }
    return `${lindanCarSelectedVehicle.name}以上，${stdJoined}`;
  }, [lindanCarSelectedVehicle, lindanCarSelectedStdList]);

  const resetLingDanInfo = () => {
    setLindanCarSelectedVehicleCb(undefined);
    setLindanCarSelectedStdListCb([]);
    carSpecificInfo.current = undefined;
    setGoodsData(undefined);
    updateLindanCarBargainPriceInfo(undefined);
  }

  return {
    otherRequire,
    setOtherRequireM,
    restReset,
    set,
    agreeGate,
    setAgreeGateData,
    resetPickUpReceivingCode,
    initPickUpReceivingCode,
    pickupCode,
    setPickupCode,
    deliveryCode,
    setDeliveryCode,
    // 纸质回单送回相关
    paperReceiptData,
    setPaperReceiptInputDataCb,
    clearPaperReceiptData,
    paperReceiptConfirmed,
    setPaperReceiptConfirmedCb,
    insuranceSelectedType,
    setInsuranceSelectedType,
    quickInsuranceData,
    setQuickInsuranceData,
    customInsuranceData,
    setCustomInsuranceData,
    clearInsuranceData,
    hitInsurance,
    selectedInsuranceData,
    carSpecificInfo,
    lindanCarSelectedVehicle,
    setLindanCarSelectedVehicleCb,
    lindanCarSelectedStdList,
    setLindanCarSelectedStdListCb,
    handleLindanCarReset,
    specInfoDisplayText,
    specLoading, setSpecLoading,
    resetLingDanInfo,
  };
}
